/**
 * B312 — the promo code's two refusals, in one place.
 *
 * The cart had a silent guard in the VIEW (`if (code !== "")`) and a second
 * one in the container, and neither said anything; separately, a code the
 * seller turned down (`cart.apply_promo` → `applied: false`) produced no
 * sentence at all, so a rejection and a press that never landed looked
 * identical. Both are the same failure — an outcome the buyer asked for and
 * did not get told — so they get ONE decision and ONE sentence channel here,
 * pure, leaving the view presentational (the §7 walk stays testable as
 * markup without a browser).
 *
 * The words are the buyer's, not the wire's: the POS says "eligibility is
 * the promotion's, not the till's" to an operator who knows what a
 * promotion is; a stranger checking out wants to know whether they typed it
 * wrong and whether their total moved.
 */
import { PROMO_CODE_MAX } from "@transact/contracts";

/** What pressing `Apply` should do — decided once, before anything is sent. */
export type PromoAttempt =
  | { readonly kind: "send"; readonly code: string }
  | { readonly kind: "refuse"; readonly why: string };

export function promoAttempt(raw: string): PromoAttempt {
  const code = raw.trim();
  if (code === "") return { kind: "refuse", why: "Enter a promo code first, then press Apply." };
  /* B1094 — longer than any code can be: said here, not as the wire's validation blob. */
  if (code.length > PROMO_CODE_MAX) return { kind: "refuse", why: `A promo code is at most ${PROMO_CODE_MAX} characters — check what you typed.` };
  return { kind: "send", code };
}

/**
 * The seller's answer was no. Names the code back so a typo is visible, and
 * states that nothing moved — B223(b)'s reasoning carried over: nothing
 * failed and nothing was charged, so this must not read like an error.
 */
export function promoRefusedWhy(code: string): string {
  return `“${code}” didn’t apply to this cart. Check the code, or carry on without it — your total hasn’t changed.`;
}
